import type { ReactNode } from "react";
import type { BookDayView, BookServiceView } from "@/design/BookAppointment";
import { DataUnavailable } from "@/design/DataUnavailable";
import { PageShell } from "@/design/ui";
import type { SocialLink } from "@/modules/catalog/surfaces";
import type { RatingSummary } from "@/modules/reviews/surfaces";
import type { ServiceLocation } from "@/modules/shared/serviceLocation";
import { NotFoundScreen } from "~/screens/NotFoundScreen";
import { PublicPage } from "~/components/SiteChrome";
import { ScreenPending } from "~/components/ScreenPending";
import { useQuery, type Query } from "~/lib/useQuery";

/**
 * What the clinic's two screens share (H59): the details view and the booking
 * view read the same screen-shaped payload, `/businesses/:id`, and wait for it
 * the same way.
 *
 * **One round trip each.** The details view ignores the availability and the
 * booking view ignores the photos; splitting the endpoint would buy two
 * endpoints that drift for a few hundred bytes, and the booking view reading
 * afresh when it opens is what keeps its times current.
 */
export interface ClinicPayload {
  business: {
    id: string;
    name: string;
    about: string | null;
    website: string | null;
    logoUrl: string | null;
    photoUrls: string[];
    socialLinks: SocialLink[];
    latitude: number;
    longitude: number;
    categoryNames: string[];
  };
  bookServices: BookServiceView[];
  availability: BookDayView[];
  availabilityByLocation: Partial<Record<ServiceLocation, BookDayView[]>>;
  offeredLocations: ServiceLocation[];
  rating: RatingSummary;
  favorite: boolean;
}

export function useClinic(id: string | undefined): Query<ClinicPayload> {
  return useQuery<ClinicPayload>(id ? `/businesses/${id}` : null);
}

/** Not found, the failure panel, the pending state, or the screen itself. */
export function ClinicFrame({
  clinic,
  children,
}: {
  clinic: Query<ClinicPayload>;
  children: (data: ClinicPayload) => ReactNode;
}) {
  if (clinic.failure === "not_found") return <NotFoundScreen />;

  if (clinic.failure) {
    return (
      <PublicPage>
        <PageShell>
          <div className="mx-auto w-full max-w-xl">
            <DataUnavailable
              configured={clinic.failure !== "not_configured"}
              what="this business"
            />
          </div>
        </PageShell>
      </PublicPage>
    );
  }

  if (!clinic.data) {
    return (
      <PublicPage>
        <ScreenPending />
      </PublicPage>
    );
  }

  return (
    <PublicPage>
      <PageShell>
        <div className="mx-auto w-full max-w-xl">{children(clinic.data)}</div>
      </PageShell>
    </PublicPage>
  );
}
